import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import LandingPage from './pages/LandingPage';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthPage } from './pages/AuthPage';
import { Dashboard } from './pages/Dashboard';
import { Customers } from './pages/Customers';
import { Vendors } from './pages/Vendors';
import { Items } from './pages/Items';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import { JobsPage } from './pages/JobsPage';
import { BillsPage } from './pages/BillsPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { EstimatesPage } from './pages/EstimatesPage';
import { PaymentsPage } from './pages/PaymentsPage';
import { ExpensesPage } from './pages/ExpensesPage';
import { BankAccountsPage } from './pages/BankAccountsPage';
import { ReportsPage } from './pages/ReportsPage';
import { CompanySettings } from './pages/CompanySettings';
import { InvoiceTemplates } from './pages/InvoiceTemplates';
import { UserManagement } from './components/admin/UserManagement';
import { RentalsPage } from './pages/RentalsPage';
import { PurchaseOrdersPage } from './pages/PurchaseOrdersPage';
import { GoodsReceiptsPage } from './pages/GoodsReceiptsPage';
import { Layout } from './components/layout/Layout';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { AuthInitializer } from './components/auth/AuthInitializer';
import { TestReports } from './components/TestReports';
import ReconciliationPage from './pages/ReconciliationPage';
import { StockTransferPage } from './pages/StockTransferPage';
import { ServiceWarehouseStockPage } from './pages/ServiceWarehouseStockPage';
import CustomerMachineReport from './components/rentals/CustomerMachineReport';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthInitializer>
          <Router>
          <div>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/customers" 
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Customers />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/vendors" 
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Vendors />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/items"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Items />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ProductsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ProductDetailPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs" 
                element={
                  <ProtectedRoute>
                    <Layout>
                      <JobsPage />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/bills"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <BillsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bills/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <BillsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/purchase-orders"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PurchaseOrdersPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/purchase-orders/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PurchaseOrdersPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/goods-receipts"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <GoodsReceiptsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/goods-receipts/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <GoodsReceiptsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invoices"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <InvoicesPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invoices/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <InvoicesPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/estimates"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <EstimatesPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/estimates/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <EstimatesPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payments" 
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PaymentsPage />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/expenses"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ExpensesPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bank-accounts"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <BankAccountsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bank-accounts/:bankAccountId/reconciliation"
                element={
                  <ProtectedRoute>
                    <ReconciliationPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ReportsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rentals"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <RentalsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rentals/list"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <RentalsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rentals/machines"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <RentalsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rentals/refill"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <RentalsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rentals/new"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <RentalsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rentals/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <RentalsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rentals/:id/edit"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <RentalsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/machine-reports"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <CustomerMachineReport />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/stock-transfer"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <StockTransferPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/service-stock"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ServiceWarehouseStockPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/company"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <CompanySettings />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/invoice-templates"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <InvoiceTemplates />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/users"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <UserManagement />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/test-reports"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <TestReports />
                    </Layout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </div>
          </Router>
        </AuthInitializer>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;